import React, { useMemo, type KeyboardEvent } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { getMonthGrid, isDateSelectable, ymd, type BookingCalendarConfig } from '../../utils/sandboxBookingCalendar';

type Props = {
    month: { y: number; m: number };
    onMonthChange: (next: { y: number; m: number }) => void;
    selectedDate: string | null;
    onSelectDate: (ymd: string) => void;
    config?: BookingCalendarConfig | null;
};

/**
 * Meeteor-style month grid for picking a bookable day.
 */
export function SandboxBookingMonthCalendar({ month, onMonthChange, selectedDate, onSelectDate, config }: Props) {
    const grid = useMemo(() => getMonthGrid(month.y, month.m), [month.y, month.m]);
    const label = useMemo(
        () =>
            new Date(month.y, month.m, 1).toLocaleString(undefined, {
                month: 'long',
                year: 'numeric',
            }),
        [month.y, month.m],
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 800,
                        fontSize: 15,
                        color: OBSIDIAN.onSurface,
                    }}>
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                    <IconButton
                        size="small"
                        onClick={() =>
                            onMonthChange(month.m === 0 ? { y: month.y - 1, m: 11 } : { y: month.y, m: month.m - 1 })
                        }
                        sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() =>
                            onMonthChange(month.m === 11 ? { y: month.y + 1, m: 0 } : { y: month.y, m: month.m + 1 })
                        }
                        sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 0.75,
                    mb: 1,
                    textAlign: 'center',
                }}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                    <Typography
                        key={d}
                        sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                            fontFamily: OBSIDIAN_BODY_FONT,
                        }}>
                        {d}
                    </Typography>
                ))}
            </Box>

            {grid.map((week, wi) => (
                <Box
                    key={wi}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: 0.75,
                        mb: 0.75,
                    }}>
                    {week.map((day, di) => {
                        if (!day) {
                            return <Box key={`pad-${wi}-${di}`} sx={{ aspectRatio: '1', minHeight: 36 }} />;
                        }
                        const ds = ymd(day);
                        const inMonth = day.getMonth() === month.m;
                        const selectable = inMonth && (!config || isDateSelectable(day, config));
                        const isSel = selectedDate === ds;
                        return (
                            <Box
                                key={ds}
                                role="button"
                                tabIndex={selectable ? 0 : -1}
                                onClick={() => {
                                    if (selectable) onSelectDate(ds);
                                }}
                                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                                    if ((e.key === 'Enter' || e.key === ' ') && selectable) {
                                        e.preventDefault();
                                        onSelectDate(ds);
                                    }
                                }}
                                sx={{
                                    py: 1,
                                    minHeight: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 2,
                                    cursor: selectable ? 'pointer' : 'default',
                                    fontSize: 13,
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: isSel ? 800 : 600,
                                    color: isSel
                                        ? OBSIDIAN.onPrimaryFixed
                                        : !inMonth
                                        ? `${OBSIDIAN.onSurfaceVariant}55`
                                        : !selectable
                                        ? `${OBSIDIAN.onSurfaceVariant}66`
                                        : OBSIDIAN.onSurface,
                                    bgcolor: isSel ? OBSIDIAN.primary : 'transparent',
                                    boxShadow: isSel ? '0 0 16px rgba(255,201,101,0.35)' : 'none',
                                    '&:hover': selectable
                                        ? {
                                              bgcolor: isSel ? OBSIDIAN.primary : OBSIDIAN.surfaceContainer,
                                          }
                                        : {},
                                }}>
                                {day.getDate()}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}
